// ═══ 미수 보드 (정산 탭 · 전체 스코프) — "오늘 뭘 챙겨야 하나" ═══
// 정본 UX: settlement-redesign-proposal.html §3 A-1 (방향 A — 미수 탭 흡수).
// 구 '미수' 탭(CancelAgencyReceivables)의 롤업/펼침·미수만 토글·이월 인라인 수정이
// 이 컴포넌트로 옮겨왔다. 검색은 좌측 거래처 패널(ClientScopePanel)이 담당한다.
// ※ 열람(정산서 링크 열람 횟수)은 표시하지 않는다 (Jin 8/20) — 보관함(CancelAgencyStatements)
//   과 동일 결정. 응답의 viewCount 는 계속 오지만 이 화면은 읽지 않는다.
//
// 데이터 (서버 무변경 — 기존 응답만 사용):
//   GET /settlement-receivables/:agencyId → 거래처별 [{kind,key,companyId,clientId,companyName,
//        openingBalance,totalBilled,totalPaid,outstanding,viewCount}] (미수 내림차순)
//   GET /statements?agencyId= → 발행 이력 [{accountId,period,changedAfterIssue,viewCount,superseded_by,...}]
//   계정/단위(billing-accounts) 와 이번 달 청구·입금·입금이력(payments)은 부모(CancelAgencySettlement)가
//   이미 들고 있는 것을 props 로 받는다 (중복 요청 금지).
//
// 행 → 계정 매핑은 구 미수 탭과 동일 규약:
//   kind='client' 행은 clientId(=work_units.id), kind='company' 행은 companyId 로 브로커 계정에 매칭.
//   direct 계정(1:1)은 평면 행, 어느 계정에도 안 붙는 레거시 행은 '기타' 섹션 — 절대 버리지 않는다.
import React, { useState, useEffect } from 'react';
import { API, authFetch } from '../utils/api';
import ClientScopeCombo from './ClientScopeCombo';
import { useIsMobile, hScroll } from '../utils/useMobile';

var fmt = function (n) { return (parseInt(n, 10) || 0).toLocaleString(); };
// 'YYYY-MM-DD' → 'M/D' (행 메타의 최근 입금일)
var mdLabel = function (v) {
  var m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(v || ''));
  return m ? (parseInt(m[2], 10) + '/' + parseInt(m[3], 10)) : String(v || '');
};
var monthNo = function (p) {
  var m = /^(\d{4})-(\d{2})$/.exec(String(p || ''));
  return m ? parseInt(m[2], 10) : 0;
};

var headSt = { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, flexWrap: 'wrap' };
var rowSt = { display: 'flex', alignItems: 'center', gap: 10, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: '9px 13px', marginBottom: 6, cursor: 'pointer' };
var nmSt = { fontWeight: 700, fontSize: 13, color: '#1e293b' };
var metaSt = { fontSize: 11, color: '#94a3b8', marginTop: 1 };
var actSt = { background: 'none', border: 'none', padding: '2px 4px', fontSize: 11.5, fontWeight: 700, color: '#2563EB', cursor: 'pointer', whiteSpace: 'nowrap' };
var dotSt = { width: 7, height: 7, borderRadius: '50%', flexShrink: 0 };
var tagSt = { marginLeft: 6, fontSize: 10, fontWeight: 700, color: '#2563EB', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 5, padding: '0 5px', whiteSpace: 'nowrap', flexShrink: 0 };
var chipSt = { fontSize: 10.5, fontWeight: 700, color: '#b45309', background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 6, padding: '1px 6px', whiteSpace: 'nowrap' };
// 펼치기 필 — ClientScopePanel 의 'N곳 펼치기 ▾/접기 ▴' 버튼과 같은 톤 (▸ 만으로는 상태 이해가 어렵다는 Jin 피드백)
var pillSt = { flexShrink: 0, marginLeft: 6, border: '1px solid #bfdbfe', background: '#fff', color: '#2563eb', borderRadius: 999, padding: '2px 9px', fontSize: 11, fontWeight: 700, cursor: 'pointer', lineHeight: 1.4, whiteSpace: 'nowrap' };
var emptySt = { padding: 32, textAlign: 'center', color: '#94a3b8', fontSize: 13, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10 };
var secSt = { fontSize: 11, fontWeight: 700, color: '#64748b', margin: '12px 0 6px' };
var outColor = function (v) { return v > 0 ? '#dc2626' : (v < 0 ? '#059669' : '#94a3b8'); };
// ── 표 (직거래·대행사·하위 행이 같은 x축) ──
// 숫자 컬럼 고정폭 + 이름 컬럼이 나머지 흡수(ellipsis) → 우측 액션까지 잘리지 않는다.
var tblCardSt = { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: '0 6px' };
var tblSt = { width: '100%', minWidth: 660, borderCollapse: 'collapse', tableLayout: 'fixed', fontSize: 12.5 };
// top:-pad = 스크롤 컨테이너(.dash-content padding — 모바일 12 / 데스크탑 24) 상단에 정확히 붙임 (정산 하위 업체 표와 동일 패턴)
// 긴 라벨('지난달 청구 (이월 포함)')은 고정폭 안에서 줄바꿈 — nowrap 이면 옆 컬럼을 침범한다.
var thBase = { padding: '7px 8px', fontSize: 11, fontWeight: 600, color: '#64748b', textAlign: 'right', lineHeight: 1.35, borderBottom: '1px solid #e5e7eb', position: 'sticky', background: '#fff', zIndex: 1 };
var thStOf = function (pad) { return Object.assign({}, thBase, { top: -pad, boxShadow: '0 -' + pad + 'px 0 0 #f0f2f5' }); };
var tdSt = { padding: '8px', borderBottom: '1px solid #f1f5f9', verticalAlign: 'middle' };
var numTdSt = Object.assign({}, tdSt, { textAlign: 'right', whiteSpace: 'nowrap' });
var ellipsisSt = { minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };
// ── 모바일 한정 (데스크탑 렌더 불변) ──
// 거래처명 열은 가로 스크롤 중에도 남긴다 — 금액 열로 밀어도 어느 업체 줄인지 보이게.
// z-order: 헤더(th 2 / 이름 th 3) > 본문 sticky 이름(td 1). 이름 td 가 헤더보다 높으면
// 세로 스크롤 시 헤더 위로 이름이 비친다.
var stkNameTd = { position: 'sticky', left: 0, background: '#fff', zIndex: 1 };
var stkKidTd = { position: 'sticky', left: 0, background: '#f5f9ff', zIndex: 1 };
var stkNameTh = { left: 0, zIndex: 3, whiteSpace: 'nowrap' };

function ReceivablesBoard(props) {
  var agencyId = parseInt(props.agencyId, 10) || 0;
  var month = props.month || '';
  var accounts = props.accounts || [];
  var unitsByAcc = props.unitsByAcc || {};
  var scopeData = props.scopeData || { groups: [], directs: [] };
  var payments = props.payments || [];
  var onPick = props.onPick || function () {};
  var onIssue = props.onIssue || function () {};
  var onPay = props.onPay || function () {};
  var onAssignPayment = props.onAssignPayment || function () {};
  var reloadKey = props.reloadKey || 0;
  // 모바일(≤768px) — 데스크탑 렌더 불변, 분기만 추가. pad = .dash-content 패딩(sticky 상쇄 기준).
  var isMobile = useIsMobile();
  var pad = isMobile ? 12 : 24;
  var thSt = thStOf(pad);
  // 행 높이 — 모바일은 행 전체가 터치 타깃(클릭 = 거래처 이동)이라 세로 패딩을 키운다 (≥40px).
  var tdC = isMobile ? Object.assign({}, tdSt, { padding: '12px 8px' }) : tdSt;
  var numTdC = isMobile ? Object.assign({}, numTdSt, { padding: '12px 8px' }) : numTdSt;
  // 액션 열 — 버튼 자체가 40px 타깃이라 셀 패딩까지 더하면 그 행만 20px 더 커진다. 버튼에 높이를 맡긴다.
  var actTdC = isMobile ? Object.assign({}, numTdC, { paddingTop: 2, paddingBottom: 2 }) : numTdSt;

  var [rows, setRows] = useState([]);
  var [statements, setStatements] = useState([]);
  var [loaded, setLoaded] = useState(false);
  var [onlyOutstanding, setOnlyOutstanding] = useState(true);
  var [expanded, setExpanded] = useState({});
  var [assignId, setAssignId] = useState(0);  // 미지정 입금 재지정 중인 payment.id
  var [assignScope, setAssignScope] = useState('');

  // 미수 행 + 발행 이력. 실패해도 빈 배열로 degrade (계정 API 미가용 시 평면 폴백과 같은 사상).
  useEffect(function () {
    if (!agencyId) { setRows([]); setStatements([]); setLoaded(true); return; }
    var alive = true;
    setLoaded(false);
    var getJson = function (url) {
      return authFetch(API + url).then(function (r) { return r.json(); }).catch(function () { return null; });
    };
    // 행 미수 = '조회 월 시점' (monthOpening − monthPaid) — 스트립·장부와 같은 시점 (통일안 v2, 2026-08-12)
    Promise.all([getJson('/settlement-receivables/' + agencyId + '?month=' + (month || new Date().toISOString().slice(0, 7))), getJson('/statements?agencyId=' + agencyId)])
      .then(function (res) {
        if (!alive) return;
        setRows(Array.isArray(res[0]) ? res[0] : []);
        setStatements(Array.isArray(res[1]) ? res[1] : []);
        setLoaded(true);
      });
    return function () { alive = false; };
  }, [agencyId, month, reloadKey]);

  // ── 미수 표시 규칙 (통일안 v2, Jin 확정 2026-08-12) — 모든 달 동일 ──
  // 행 미수 = 그 달 opening − 그 달 입금 (스트립·장부·패널과 같은 시점). 그 달 청구는 '+예상' 꼬리표로만.
  var isCurMonth = month === new Date().toISOString().slice(0, 7);
  // 금액 열 순서 — 모바일은 '미수'를 거래처명 바로 옆으로 당긴다. 데스크탑 순서(청구→입금→미수→청구)
  // 그대로면 390px 에서 미수가 화면 밖이라 매번 오른쪽으로 밀어야 보이는데, 이 표를 보는 이유가 그 숫자다.
  // (정산 화면 하위 업체 표와 같은 처방)
  var cols = isMobile ? ['out', 'mOpening', 'mPaid', 'mCharged'] : ['mOpening', 'mPaid', 'out', 'mCharged'];
  // 모바일 헤더는 축약 — 긴 라벨이 2~3줄로 접히면 표가 세로로 부풀고 좁은 열을 더 잠식한다.
  // 전체 문구는 상단 4칸 스트립(부모)이 이미 들고 있다.
  var colLabels = {
    mOpening: isMobile ? '지난달 청구' : '지난달 청구 (이월 포함)',
    mPaid: isMobile ? '입금' : ((isCurMonth ? '이번 달' : (monthNo(month) + '월')) + ' 입금'),
    out: '미수',
    mCharged: isMobile ? (isCurMonth ? '청구 · 예상' : '청구') : (isCurMonth ? '이번 달 청구 · 예상' : (monthNo(month) + '월 청구')),
  };
  // 행의 '예상 청구' — monthCharged 미제공(구 서버)이면 0 → 표시는 종전 미수로 폴백된다.
  var estOf = function (o) { return parseInt(o && o.monthCharged, 10) || 0; };
  // 계정(브로커) 예상 청구 — 계정 응답 우선(계정 레벨 전기 포함), 없으면 하위 행 합.
  var groupEst = function (a, kids) {
    if (a && a.monthCharged !== undefined && a.monthCharged !== null) return parseInt(a.monthCharged, 10) || 0;
    return kids.reduce(function (s, rc) { return s + estOf(rc); }, 0);
  };
  // ── 표 숫자 컬럼 원천 — 조회 월 흐름값 (누계 openingBalance/totalBilled/totalPaid 는 표에 쓰지 않는다) ──
  // 구 서버(흐름값 미제공) 는 null → '-' 로 표시. 정산 하위 업체 표의 hasFlow 규약과 동일.
  var flowOf = function (o, key) {
    return (o && o[key] !== undefined && o[key] !== null) ? (parseInt(o[key], 10) || 0) : null;
  };
  // 계정 레벨 흐름값 — 계정 응답 우선, 없으면 하위 행 합(하나라도 제공될 때만).
  var groupFlow = function (a, kids, key) {
    var v = flowOf(a, key);
    if (v !== null) return v;
    var any = false;
    var s = kids.reduce(function (t, rc) {
      var k = flowOf(rc, key);
      if (k === null) return t;
      any = true; return t + k;
    }, 0);
    return any ? s : null;
  };

  // ── 행 → 계정 매핑 (구 미수 탭 규약 그대로) ──
  var brokerAccs = accounts.filter(function (a) { return a.kind === 'broker'; });
  var unitAcc = {}, compAcc = {};
  brokerAccs.forEach(function (a) {
    (unitsByAcc[a.id] || []).forEach(function (u) {
      unitAcc[u.id] = a.id;
      if ((u.companyId || 0) > 0 && !compAcc[u.companyId]) compAcc[u.companyId] = a.id;
    });
  });
  var directComp = {}, directName = {}, directAccByComp = {}, directAccByName = {};
  accounts.forEach(function (a) {
    if (a.kind === 'broker') return;
    if ((a.companyId || 0) > 0) { directComp[a.companyId] = 1; directAccByComp[a.companyId] = a; }
    var nm = String(a.name || '').trim();
    directName[nm] = 1; directAccByName[nm] = a;
  });

  var brokerIds = {};
  brokerAccs.forEach(function (a) { brokerIds[a.id] = 1; });

  var kidsByAcc = {}, flatRows = [], otherRows = [];
  rows.forEach(function (rc) {
    if (!accounts.length) { flatRows.push(rc); return; }
    // kind='account' = 계정 레벨(unit_id=0) 전기 행 (2026-08-17 서버 추가 — 대행업체 이름 통합 입금 등).
    // 브로커는 **그룹 행이 계정 응답(monthOpening/balance)으로 이미 계정 레벨 전기를 품고 있다**
    // → 하위로만 넣는다. 평면으로 올리면 총계에 두 번 들어간다.
    // 브로커가 아닌 계정('(미지정)' 등)은 그룹이 없으니 평면 행이 유일한 표시처.
    if (rc.kind === 'account') {
      if (brokerIds[rc.accountId]) (kidsByAcc[rc.accountId] = kidsByAcc[rc.accountId] || []).push(rc);
      else flatRows.push(rc);
      return;
    }
    var accId = rc.kind === 'client' ? (unitAcc[rc.clientId] || 0) : (compAcc[rc.companyId] || 0);
    if (accId) { (kidsByAcc[accId] = kidsByAcc[accId] || []).push(rc); return; }
    var nm2 = String(rc.companyName || '').trim();
    if (((rc.companyId || 0) > 0 && directComp[rc.companyId]) || directName[nm2]) flatRows.push(rc);
    else otherRows.push(rc);
  });

  // ── 발행 상태 색인 — 계정별 이번 달 발행 여부 / 재발행 필요 ('다음 액션' 판정용) ──
  var stByAcc = {};
  statements.forEach(function (st) {
    if ((st.superseded_by || 0) > 0) return;
    var a = stByAcc[st.accountId] || (stByAcc[st.accountId] = { issuedThisMonth: false, changed: false });
    if (st.period === month) { a.issuedThisMonth = true; if (st.changedAfterIssue) a.changed = true; }
  });

  // ── 이번 달 입금 — 스코프 키별 최근 입금일 + 미지정 건 ──
  var payKey = function (p) {
    if ((p.companyId || 0) > 0) return 'c' + p.companyId;
    if ((p.clientId || 0) > 0) return 'w' + p.clientId;
    return '';
  };
  var lastPayBy = {};
  var unassignedPays = [];
  payments.forEach(function (p) {
    // 대행업체(브로커) 이름으로 받은 통합 입금도 좌표가 없어 여기 걸리던 것을 서버 unassigned 로 가른다
    // (2026-08-17). 그 입금은 계정에 정상 귀속돼 있으므로 '거래처를 지정해 주세요' 대상이 아니다.
    // 구 서버는 키 부재(undefined) → 종전대로 좌표만으로 판정.
    if (p.unassigned === false) return;
    var k = payKey(p);
    if (!k) { unassignedPays.push(p); return; }
    if (!lastPayBy[k] || String(p.date || '') > lastPayBy[k]) lastPayBy[k] = String(p.date || '');
  });
  var lastPayOf = function (keys) {
    var best = '';
    keys.forEach(function (k) { if (lastPayBy[k] && lastPayBy[k] > best) best = lastPayBy[k]; });
    return best;
  };

  // ── 보드 행 구성 ── (미수 = 확정분, 이번 달 청구는 '예상' 꼬리표로만)
  // 그 달 미수 — monthOpening/monthPaid(신 서버) 우선, 없으면 종전 산식 폴백.
  var confirmedOf = function (rc) {
    if (rc && rc.monthOpening !== undefined && rc.monthOpening !== null) return (rc.monthOpening || 0) - (rc.monthPaid || 0);
    return (rc.outstanding || 0) - estOf(rc);
  };
  var visible = function (rc) { return !(onlyOutstanding && confirmedOf(rc) === 0); };
  var boardRows = [];
  brokerAccs.forEach(function (a) {
    var kids = kidsByAcc[a.id] || [];
    var agg = { opening: 0, billed: 0, paid: 0 };
    var keys = [];
    kids.forEach(function (rc) {
      agg.opening += (rc.openingBalance || 0); agg.billed += (rc.totalBilled || 0);
      agg.paid += (rc.totalPaid || 0);
      keys.push(rc.kind === 'client' ? ('w' + rc.clientId) : ('c' + rc.companyId));
    });
    var est = groupEst(a, kids);
    var out = (a.monthOpening !== undefined && a.monthOpening !== null)
      ? (a.monthOpening || 0) - (a.monthPaid || 0)
      : (typeof a.balance === 'number' ? a.balance : (agg.opening + agg.billed - agg.paid)) - est;
    var visKids = kids.filter(visible);
    if (onlyOutstanding && out === 0 && visKids.length === 0) return;
    boardRows.push({
      type: 'group', id: 'a' + a.id, scope: 'a:' + a.id, name: a.name, accountId: a.id,
      unitsCount: a.unitsCount || kids.length, outstanding: out, kids: visKids,
      mOpening: groupFlow(a, kids, 'monthOpening'), mPaid: groupFlow(a, kids, 'monthPaid'),
      mCharged: groupFlow(a, kids, 'monthCharged'),
      lastPay: lastPayOf(keys)
    });
  });
  // 회사 연결 단위 역색인 — 계정 매칭이 안 되는 레거시 행도 단위 스코프로 열 수 있게 (구 미수 탭의 'c:' 변환 대응)
  var unitByCompany = {};
  Object.keys(unitsByAcc).forEach(function (aid) {
    (unitsByAcc[aid] || []).forEach(function (u) {
      var cid = parseInt(u.companyId, 10) || 0;
      if (cid > 0 && !unitByCompany[cid]) unitByCompany[cid] = u.id;
    });
  });
  var rowScope = function (rc) {
    if ((rc.clientId || 0) > 0) return 'u:' + rc.clientId;
    var acc = directAccByComp[rc.companyId] || directAccByName[String(rc.companyName || '').trim()];
    if (acc) {
      var us = unitsByAcc[acc.id] || [];
      return us.length ? ('u:' + us[0].id) : ('a:' + acc.id);
    }
    return unitByCompany[rc.companyId] ? ('u:' + unitByCompany[rc.companyId]) : '';
  };
  var rowAccount = function (rc) {
    return directAccByComp[rc.companyId] || directAccByName[String(rc.companyName || '').trim()] || null;
  };
  flatRows.concat(otherRows).forEach(function (rc) {
    if (!visible(rc)) return;
    var acc = rowAccount(rc);
    boardRows.push({
      type: 'direct', id: rc.key || (rc.kind + rc.companyId + '-' + rc.clientId), scope: rowScope(rc),
      name: rc.companyName, accountId: acc ? acc.id : 0, rc: rc,
      outstanding: confirmedOf(rc),
      mOpening: flowOf(rc, 'monthOpening'), mPaid: flowOf(rc, 'monthPaid'), mCharged: flowOf(rc, 'monthCharged'),
      lastPay: lastPayOf([rc.kind === 'client' ? ('w' + rc.clientId) : ('c' + rc.companyId)])
    });
  });
  boardRows.sort(function (x, y) { return (y.outstanding || 0) - (x.outstanding || 0); });



  var toggle = function (e, id) {
    e.stopPropagation();
    setExpanded(function (prev) {
      var n = Object.assign({}, prev); n[id] = !prev[id]; return n;
    });
  };

  var nextAction = function (r) {
    var st = stByAcc[r.accountId];
    if (r.accountId && (!st || !st.issuedThisMonth || st.changed)) return { label: (st && st.changed) ? '재발행 →' : '발행 →', kind: 'issue' };
    return { label: '입금 →', kind: 'pay' };
  };

  // 숫자 셀 — 흐름값 미제공(구 서버)이면 '-'. 정산 하위 업체 표와 같은 표기.
  var numTd = function (v, extra, k) {
    return (<td key={k} style={Object.assign({}, numTdC, extra || {})}>{v === null ? '-' : (fmt(v) + '원')}</td>);
  };

  // 하위(kid) 행 — 대행사 아래 업체. 숫자는 전부 '조회 월' 기준 (누계 아님, Jin 8/12).
  var kidRow = function (rc) {
    var sc = rowScope(rc) || ((rc.clientId || 0) > 0 ? 'u:' + rc.clientId : '');
    var out = confirmedOf(rc);
    var nameCell = (<td style={Object.assign({}, tdC, { paddingLeft: 0 }, isMobile ? stkKidTd : {})}>
      <div style={Object.assign({ borderLeft: '3px solid #60a5fa', marginLeft: 12, paddingLeft: 11, fontWeight: 600, color: '#334155' }, ellipsisSt)}>{rc.companyName}</div>
    </td>);
    // 개시 이월 ✏ 수정은 노출 제거 (Jin 8/12 — 장부 임의 수정으로 오독·오조작 위험). 정정은 수기 조정 전기로.
    var kidCells = {
      mOpening: numTd(flowOf(rc, 'monthOpening'), { color: '#334155' }, 'o'),
      mPaid: numTd(flowOf(rc, 'monthPaid'), { color: '#059669' }, 'p'),
      out: (<td key="b" style={Object.assign({}, numTdC, { fontWeight: 700, color: outColor(out) })}>{fmt(Math.abs(out)) + '원'}</td>),
      mCharged: numTd(flowOf(rc, 'monthCharged'), { color: '#94a3b8' }, 'c'),
    };
    return (<tr key={rc.key || (rc.kind + rc.companyId + '-' + rc.clientId)} style={{ background: '#f5f9ff', cursor: sc ? 'pointer' : 'default' }}
      onClick={function () { if (sc) onPick(sc); }}>
      {nameCell}
      {cols.map(function (k) { return kidCells[k]; })}
      <td style={numTdC} />
    </tr>);
  };

  // 미지정 입금 — 거래처를 못 정한 채 기록된 입금. 여기서 바로 재지정(PATCH)한다.
  // ⚠ 브로커 계정 레벨 입금(단위 미지정)도 서버 응답상 (미지정) 으로 온다 — 기존 입금 이력 표기와 동일.
  var payRow = function (p) {
    var editing = assignId === p.id;
    // 모바일 — 카드가 좁아 [칩 · 내용 · 재지정 UI] 가 한 줄에 안 들어간다 → 줄바꿈 + 재지정 UI 전폭
    return (<div key={'p' + p.id} style={Object.assign({}, rowSt, { borderColor: '#fcd34d', background: '#fffbeb', cursor: 'default' }, isMobile ? { flexWrap: 'wrap' } : {})}>
      <span style={chipSt}>(미지정)</span>
      <div style={{ minWidth: 0 }}>
        <div style={nmSt}>미지정 입금 {fmt(p.amount)}원</div>
        <div style={metaSt}>{mdLabel(p.date)} 등록{p.memo ? ' · ' + p.memo : ''} · 거래처를 지정해 주세요</div>
      </div>
      {editing
        ? (<span style={isMobile
            ? { width: '100%', display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }
            : { marginLeft: 'auto', display: 'flex', gap: 4, alignItems: 'center' }}>
            <span style={isMobile ? { width: '100%' } : { width: 200 }}><ClientScopeCombo value={assignScope} data={scopeData}
              onChange={function (v) { setAssignScope(v); }} emptyLabel="(미지정)" placeholder="거래처 검색..." /></span>
            <button className="btn btn-blue btn-sm" style={isMobile ? { flex: 1, height: 40, justifyContent: 'center' } : undefined} onClick={function () {
              onAssignPayment(p.id, assignScope, function () { setAssignId(0); setAssignScope(''); });
            }}>저장</button>
            <button className="btn btn-outline btn-sm" style={isMobile ? { flex: 1, height: 40, justifyContent: 'center' } : undefined} onClick={function () { setAssignId(0); setAssignScope(''); }}>취소</button>
          </span>)
        : (<button style={Object.assign({ marginLeft: 'auto' }, actSt, isMobile ? { padding: '10px 8px' } : {})} onClick={function () { setAssignId(p.id); setAssignScope(''); }}>지정 →</button>)}
    </div>);
  };

  return (<div>
    {/* 상단 집계는 부모(정산 화면)의 공통 4칸 스트립이 담당 — 보드 자체 스트립·'챙길 것' 제거 (Jin 8/12) */}

    <div style={headSt}>
      <span style={{ fontSize: 12, color: '#64748b' }}>거래처 {boardRows.length}곳 · 미수 내림차순</span>
      <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: '#475569', cursor: 'pointer', marginLeft: 'auto' }}>
        <input type="checkbox" checked={onlyOutstanding} onChange={function (e) { setOnlyOutstanding(e.target.checked); }} /> 미수만 보기
      </label>
    </div>

    {/* 미지정 입금 — 예외를 맨 위로 */}
    {unassignedPays.map(payRow)}

    {/* 거래처 표 — 직거래·대행사·하위 행이 모두 같은 컬럼 축 (Jin 8/12) */}
    {/* 모바일 — 표는 그대로 두고 카드 래퍼에서만 가로 스크롤 (페이지 가로 스크롤 금지).
        colgroup 퍼센트는 minWidth 기준으로 그대로 유지된다. */}
    {boardRows.length > 0 && <div style={isMobile ? Object.assign({}, tblCardSt, hScroll) : tblCardSt}>
      {/* 모바일 minWidth 700 — 660 이면 숫자 열(14.5% = 95px)이 '12,345,678원' 을 못 담아 옆 열과 겹친다 */}
      <table style={isMobile ? Object.assign({}, tblSt, { minWidth: 700 }) : tblSt}>
        <colgroup>
          {/* 업체명 30% · 숫자 4×14.5% · 액션 12% — 이름 컬럼 과대 방지 (Jin 8/12) */}
          <col style={{ width: '30%' }} />
          <col style={{ width: '14.5%' }} /><col style={{ width: '14.5%' }} /><col style={{ width: '14.5%' }} /><col style={{ width: '14.5%' }} />
          <col style={{ width: '12%' }} />
        </colgroup>
        <thead><tr>
          <th style={Object.assign({}, thSt, { textAlign: 'left' }, isMobile ? stkNameTh : {})}>거래처</th>
          {cols.map(function (k) {
            return (<th key={k} style={isMobile ? Object.assign({}, thSt, { zIndex: 2 }) : thSt}>{colLabels[k]}</th>);
          })}
          <th style={isMobile ? Object.assign({}, thSt, { zIndex: 2 }) : thSt} />
        </tr></thead>
        <tbody>
          {boardRows.map(function (r) {
            var act = nextAction(r);
            var open = !!expanded[r.id];
            return (<React.Fragment key={r.id}>
              <tr style={{ cursor: r.scope ? 'pointer' : 'default' }} onClick={function () { if (r.scope) onPick(r.scope); }}>
                <td style={isMobile ? Object.assign({}, tdC, stkNameTd) : tdC}>
                  <div style={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
                    {r.type === 'direct' &&
                      <span style={Object.assign({ background: (r.outstanding || 0) > 0 ? '#dc2626' : '#cbd5e1', marginRight: 7 }, dotSt)} />}
                    <span style={Object.assign({}, nmSt, ellipsisSt)}>{r.name}</span>
                    {/* 모바일 — 뱃지·펼치기 필이 이름과 한 줄을 다투면 업체명이 '온커머...' 로 잘린다. 아래 줄로 내린다 */}
                    {r.type === 'group' && !isMobile && <span style={tagSt}>대행사 {r.unitsCount}곳</span>}
                    {/* 펼침/접힘 — 라벨 있는 필 (좌측 거래처 패널과 같은 규약) */}
                    {r.type === 'group' && !isMobile &&
                      <button type="button" title={open ? '하위 업체 접기' : '하위 업체 펼치기'}
                        style={Object.assign({}, pillSt, open ? { background: '#dbeafe' } : {})}
                        onClick={function (e) { toggle(e, r.id); }}>{open ? '접기 ▴' : '펼치기 ▾'}</button>}
                  </div>
                  {r.type === 'group' && isMobile &&
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 5 }}>
                      <span style={Object.assign({}, tagSt, { marginLeft: 0 })}>대행사 {r.unitsCount}곳</span>
                      <button type="button" title={open ? '하위 업체 접기' : '하위 업체 펼치기'}
                        style={Object.assign({}, pillSt, open ? { background: '#dbeafe' } : {},
                          { marginLeft: 0, padding: '0 12px', minHeight: 36, display: 'inline-flex', alignItems: 'center' })}
                        onClick={function (e) { toggle(e, r.id); }}>{open ? '접기 ▴' : '펼치기 ▾'}</button>
                    </div>}
                  {/* 메타(입금/발행) 표시 제거 — 업체명만 깔끔하게 (Jin 8/12) */}
                </td>
                {(function () {
                  var cells = {
                    mOpening: numTd(r.mOpening, { color: '#334155' }, 'o'),
                    mPaid: numTd(r.mPaid, { color: '#059669' }, 'p'),
                    out: (<td key="b" style={Object.assign({}, numTdC, { fontWeight: 800, fontSize: 13.5, color: outColor(r.outstanding || 0) })}>
                      {fmt(Math.abs(r.outstanding || 0)) + '원'}
                    </td>),
                    mCharged: numTd(r.mCharged, { color: '#94a3b8' }, 'c'),
                  };
                  return cols.map(function (k) { return cells[k]; });
                })()}
                {/* 다음 액션 — 스코프를 못 찾는 레거시 행은 액션 없이 표시만 */}
                <td style={actTdC}>
                  {!!r.scope && <button style={isMobile
                    ? Object.assign({}, actSt, { padding: '0 4px', minHeight: 40, display: 'inline-flex', alignItems: 'center' })
                    : actSt} onClick={function (e) {
                    e.stopPropagation();
                    if (act.kind === 'issue') onIssue(r.scope); else onPay(r.scope);
                  }}>{act.label}</button>}
                </td>
              </tr>
              {r.type === 'group' && open && r.kids.map(kidRow)}
              {r.type === 'group' && open && r.kids.length === 0 &&
                <tr style={{ background: '#f5f9ff' }}>
                  <td style={Object.assign({}, tdC, { color: '#94a3b8', fontSize: 12 })} colSpan={6}>표시할 하위 업체가 없습니다.</td>
                </tr>}
            </React.Fragment>);
          })}
        </tbody>
      </table>
    </div>}

    {boardRows.length === 0 && unassignedPays.length === 0 &&
      <div style={emptySt}>{!loaded ? '불러오는 중…' : (rows.length === 0 ? '거래처 미수 내역이 없습니다.' : '미수가 있는 거래처가 없습니다.')}</div>}

    <p style={secSt}>행 클릭 = 대상을 그 거래처로 이동(화면은 현황 유지 — 장부는 헤더 세그먼트) · [펼치기 ▾] = 대행사 하위 업체 · 미수 = 지난달 청구(이월 포함) − 그 달 입금, 그 달 청구는 별도 컬럼(미수 미포함)</p>
  </div>);
}

export default ReceivablesBoard;
